"use client"

import * as React from "react"
import { useSearchParams } from "next/navigation"

import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useInvoices } from "@/features/invoices/hooks"
import {
  parseHomeInvoiceListState,
  toInvoiceQueryParams,
  type InvoiceListFilterState,
} from "@/features/invoices/filters"
import { useHomeInvoiceFiltersUrlSync } from "@/features/invoices/use-invoice-filters-url-sync"

import { InvoiceFilters } from "./InvoiceFilters"
import { InvoicePagination } from "./InvoicePagination"
import { InvoiceTable } from "./InvoiceTable"

const SEARCH_DEBOUNCE_MS = 300

export function InvoiceListSection() {
  const searchParams = useSearchParams()

  // Seed the full filter + page state from the URL once, on mount. Every
  // later URL write comes from this component via
  // `useHomeInvoiceFiltersUrlSync`, so a `/` → detail → back navigation
  // remounts this section and restores every control from the URL.
  const [filters, setFilters] = React.useState<InvoiceListFilterState>(() =>
    parseHomeInvoiceListState(new URLSearchParams(searchParams?.toString() ?? "")),
  )

  const debouncedSearch = useDebouncedValue(
    filters.searchText,
    SEARCH_DEBOUNCE_MS,
  )

  const queryParams = React.useMemo(
    () =>
      toInvoiceQueryParams({
        searchText: debouncedSearch,
        status: filters.status,
        paymentMode: filters.paymentMode,
        actionRequired: filters.actionRequired,
        datePreset: filters.datePreset,
        courseId: filters.courseId,
        pageSize: filters.pageSize,
        page: filters.page,
      }),
    [
      debouncedSearch,
      filters.status,
      filters.paymentMode,
      filters.actionRequired,
      filters.datePreset,
      filters.courseId,
      filters.pageSize,
      filters.page,
    ],
  )

  const query = useInvoices({
    status: queryParams.status,
    paymentMode: queryParams.payment_mode,
    courseId: queryParams.course_id,
    createdAtFrom: queryParams.created_at_from,
    createdAtTo: queryParams.created_at_to,
    search: queryParams.search,
    createdBy: "me",
    page: filters.page,
    pageSize: filters.pageSize,
  })

  // Mirror the committed filter + page state into the URL (owned keys only,
  // foreign params preserved). The debounced search keeps the URL tracking
  // what is actually queried rather than each keystroke.
  useHomeInvoiceFiltersUrlSync({ ...filters, searchText: debouncedSearch })

  const handleFilterChange = React.useCallback(
    (patch: Partial<InvoiceListFilterState>) => {
      setFilters((prev) => {
        const next = { ...prev, ...patch }
        if (!("page" in patch)) {
          next.page = 1
        }
        return next
      })
    },
    [],
  )

  const items = query.data?.items ?? []
  const count = query.data?.count ?? 0
  const loading = query.isPending
  const error = query.error

  return (
    <section aria-labelledby="invoice-list-heading">
      <div className="flex items-center justify-between mb-4">
        <h2 id="invoice-list-heading" className="text-h2 text-foreground">
          My Invoices
        </h2>
      </div>

      <InvoiceFilters value={filters} onChange={handleFilterChange} />

      {error && (
        <div
          role="alert"
          className="border border-destructive/40 bg-destructive/5 text-destructive rounded-md px-4 py-3 mb-4"
        >
          Failed to load invoices: {error.message}
        </div>
      )}

      {loading ? (
        <div className="border border-border rounded-lg px-4 py-12 text-center text-body text-muted-foreground">
          Loading invoices…
        </div>
      ) : items.length === 0 ? (
        !error && (
          <div className="border border-border rounded-lg px-4 py-12 text-center text-body text-muted-foreground">
            No invoices match the current filters.
          </div>
        )
      ) : (
        <InvoiceTable invoices={items} rowClickBehavior="navigate" />
      )}

      <InvoicePagination
        page={filters.page}
        pageSize={filters.pageSize}
        count={count}
        disabled={loading}
        onPageChange={(page) => handleFilterChange({ page })}
        onPageSizeChange={(pageSize) => handleFilterChange({ pageSize })}
      />
    </section>
  )
}
